import React, { useState } from 'react';
import { toast } from 'sonner';
import { useVenue } from '../../context/VenueContext';
import { adminApi } from '../../services/api';
import { MapPin, Plus, Pencil, X, Check, Building, Clock, Phone, Globe, Trash2 } from 'lucide-react';
import { PageTooltip } from '../components/TooltipSystem';

const DAYS = [
  { key: 'mon', label: 'Lunedì' }, { key: 'tue', label: 'Martedì' },
  { key: 'wed', label: 'Mercoledì' }, { key: 'thu', label: 'Giovedì' },
  { key: 'fri', label: 'Venerdì' }, { key: 'sat', label: 'Sabato' },
  { key: 'sun', label: 'Domenica' },
];

export default function AdminVenues() {
  const { venues, refreshVenues } = useVenue();
  const [editing, setEditing] = useState(null); // null | 'new' | venue_id
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const startNew = () => {
    setForm({ name: '', address: { full: '', city: '', province: '' }, contacts: { phone: '', email: '' }, standard_hours: {} });
    setEditing('new');
  };

  const startEdit = (v) => {
    setForm({ name: v.name, address: v.address || {}, contacts: v.contacts || {}, standard_hours: v.standard_hours || {} });
    setEditing(v.id);
  };

  const handleSave = async () => {
    if (!form.name?.trim()) { toast.error('Nome obbligatorio'); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        await adminApi.post('/admin/venues', form);
        toast.success('Punto vendita creato!');
      } else {
        await adminApi.put(`/admin/venues/${editing}`, form);
        toast.success('Aggiornato!');
      }
      await refreshVenues();
      setEditing(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (v) => {
    if (venues.length <= 1) {
      toast.error("Non puoi eliminare l'ultimo punto vendita");
      return;
    }
    if (!window.confirm(`Eliminare definitivamente "${v.name}"?\n\nQuesta azione non può essere annullata.`)) return;
    try {
      await adminApi.delete(`/admin/venues/${v.id}`);
      toast.success('Punto vendita eliminato');
      await refreshVenues();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'eliminazione");
    }
  };

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-4xl" data-testid="admin-venues-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl tracking-tight font-bold text-[#1A1A1A] flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#FDECE7] flex items-center justify-center">
              <Building size={20} className="text-[#D44E20]" />
            </div>
            Punti Vendita
          </h1>
          <p className="text-[#64748B] text-sm mt-1">Gestisci i tuoi locali</p>
        </div>
        <button
          onClick={startNew}
          data-testid="add-venue-btn"
          className="flex items-center gap-2 px-4 py-2.5 bg-[#D44E20] text-white rounded-xl text-sm font-medium hover:bg-[#B8421B] transition-colors"
        >
          <Plus size={16} /> Nuovo PV
        </button>
      </div>

      {/* Venue cards */}
      <div className="space-y-4">
        {venues.map(v => (
          <div
            key={v.id}
            className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-shadow"
            data-testid={`venue-card-${v.id}`}
          >
            {editing === v.id ? (
              <VenueForm form={form} setForm={setForm} onSave={handleSave} onCancel={() => setEditing(null)} saving={saving} />
            ) : (
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#FDECE7] flex items-center justify-center flex-shrink-0">
                  <MapPin size={20} className="text-[#D44E20]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-[#1A1A1A]">{v.name}</h3>
                  {v.address?.full && (
                    <p className="text-sm text-[#64748B] mt-0.5">{v.address.full}</p>
                  )}
                  <div className="flex flex-wrap gap-3 mt-2">
                    {v.contacts?.phone && (
                      <span className="text-xs text-[#64748B] flex items-center gap-1"><Phone size={11} /> {v.contacts.phone}</span>
                    )}
                    {v.contacts?.email && (
                      <span className="text-xs text-[#64748B] flex items-center gap-1"><Globe size={11} /> {v.contacts.email}</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => startEdit(v)}
                  data-testid={`edit-venue-${v.id}`}
                  className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-50 text-[#64748B] hover:bg-[#FDECE7] hover:text-[#D44E20] transition-colors"
                >
                  <Pencil size={14} />
                </button>
                {venues.length > 1 && (
                  <button
                    onClick={() => handleDelete(v)}
                    data-testid={`delete-venue-${v.id}`}
                    title="Elimina punto vendita"
                    className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-50 text-[#64748B] hover:bg-red-50 hover:text-red-600 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            )}
          </div>
        ))}

        {/* New venue form */}
        {editing === 'new' && (
          <div className="bg-white rounded-2xl border-2 border-dashed border-[#D44E20]/30 p-5">
            <h3 className="text-sm font-semibold text-[#D44E20] mb-4">Nuovo Punto Vendita</h3>
            <VenueForm form={form} setForm={setForm} onSave={handleSave} onCancel={() => setEditing(null)} saving={saving} />
          </div>
        )}
      </div>
    </div>
  );
}

function VenueForm({ form, setForm, onSave, onCancel, saving }) {
  const u = (field, val) => setForm(f => ({ ...f, [field]: val }));
  const uAddr = (field, val) => setForm(f => ({ ...f, address: { ...f.address, [field]: val } }));
  const uCont = (field, val) => setForm(f => ({ ...f, contacts: { ...f.contacts, [field]: val } }));

  return (
    <div className="space-y-4">
      <PageTooltip page="venues" />
      <div>
        <label className="block text-xs font-medium text-[#1A1A1A] mb-1">Nome</label>
        <input data-testid="venue-name-input" value={form.name || ''} onChange={e => u('name', e.target.value)} placeholder="Es: Taco Loco - Via del Corso"
          className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/20 focus:border-[#D44E20]" />
      </div>
      <div>
        <label className="block text-xs font-medium text-[#1A1A1A] mb-1">Indirizzo completo <span className="text-[10px] text-[#64748B] font-normal">(come appare su Google Maps)</span></label>
        <input data-testid="venue-address-input" value={form.address?.full || ''} onChange={e => uAddr('full', e.target.value)} placeholder="Via del Corso 31, Roma"
          className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/20 focus:border-[#D44E20]" />
      </div>
      <div>
        <label className="block text-xs font-medium text-[#1A1A1A] mb-1">Via e numero civico <span className="text-[10px] text-[#64748B] font-normal">(necessario per shop/delivery)</span></label>
        <input data-testid="venue-street-input" value={form.address?.street || ''} onChange={e => uAddr('street', e.target.value)} placeholder="Via del Corso, 31"
          className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/20 focus:border-[#D44E20]" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-[#1A1A1A] mb-1">Città</label>
          <input data-testid="venue-city-input" value={form.address?.city || ''} onChange={e => uAddr('city', e.target.value)} placeholder="Roma"
            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/20 focus:border-[#D44E20]" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#1A1A1A] mb-1">CAP</label>
          <input data-testid="venue-zip-input" value={form.address?.zip || ''} onChange={e => uAddr('zip', e.target.value)} placeholder="00186"
            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/20 focus:border-[#D44E20]" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#1A1A1A] mb-1">Provincia</label>
          <input value={form.address?.province || ''} onChange={e => uAddr('province', e.target.value)} placeholder="RM"
            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/20 focus:border-[#D44E20]" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-[#1A1A1A] mb-1">Telefono</label>
          <input value={form.contacts?.phone || ''} onChange={e => uCont('phone', e.target.value)} placeholder="+39 06 1234567"
            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/20 focus:border-[#D44E20]" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#1A1A1A] mb-1">Email</label>
          <input value={form.contacts?.email || ''} onChange={e => uCont('email', e.target.value)} placeholder="info@locale.it"
            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/20 focus:border-[#D44E20]" />
        </div>
      </div>
      <div className="flex gap-2 justify-end pt-2">
        <button onClick={onCancel} className="px-4 py-2 rounded-xl text-sm text-[#64748B] hover:bg-slate-50 transition-colors">Annulla</button>
        <button onClick={onSave} disabled={saving} data-testid="save-venue-btn"
          className="px-4 py-2 rounded-xl text-sm font-medium bg-[#D44E20] text-white hover:bg-[#B8421B] transition-colors disabled:opacity-50">
          {saving ? 'Salvataggio...' : 'Salva'}
        </button>
      </div>
    </div>
  );
}
